import { useEffect, useState } from "react";
import { Paper, Typography, Box, Chip, Button } from "@mui/material";
import { orderStatus, OrderTypeEnum } from "enums/shared";
import formatDateForInput from "utils/formatDateForInput";
import { useOrderContext } from "contexts/OrderContext";
import convertToArabicNumbers from "utils/convertToArabicNumbers";
import { useRTL } from "hooks/isRTL";
import LoadingBanner from "Components/shared/LoadingBanner";
import { useNavigate } from "react-router-dom";
import GroupRemoveIcon from "@mui/icons-material/GroupRemove";
/**
 * Display a banner with order details,
 */
const OrderDetailsBanner = () => {
  const { orderDetails, isLoading, isError, orderId } = useOrderContext();

  const [formattedDates, setFormattedDates] = useState([]);
  const { t, isRTL } = useRTL();
  const navigate = useNavigate();

  /**
   * Formats the order-related dates for display.
   */
  useEffect(() => {
    if (orderDetails) {
      const formatted = [
        {
          label: t("placedOn"),
          value: formatDateForInput(orderDetails.creationDate),
        },
        {
          label: t("updated"),
          value: formatDateForInput(orderDetails.modificationDate),
        },
        {
          label: t("pickupDateTime"),
          value: formatDateForInput(orderDetails.preferredPickUpDate),
        },
        {
          label: t("deliveryDateTime"),
          value: formatDateForInput(orderDetails.preferredDeliveryDate),
        },
      ];
      setFormattedDates(formatted);
    }
  }, [orderDetails, t]);

  /**
   * Handels navigationto the dispatch page
   * This action only available to awaiting Selection orders
   */
  const handleDispatchClick = () => {
    if (orderDetails?.status === 3) {
      navigate(`/orders/${orderId}/dispatch`);
    }
  };

  if (isLoading) return <LoadingBanner />;
  if (isError) return <Typography>Error fetching order details</Typography>;

  return (
    <Paper
      elevation={2}
      sx={{
        padding: "1.5rem",
        marginBottom: "1.5rem",
        borderRadius: "8px",
        backgroundColor: "background.white",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",

          marginBottom: "1rem",
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Typography variant="h2">
              {t("orderNumber")}
              {isRTL
                ? ` ${convertToArabicNumbers(orderDetails.orderNumber)}`
                : orderDetails.orderNumber}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {orderDetails?.type === OrderTypeEnum.expedited ? t("expedited") : t("regular")}
            </Typography>
          </Box>

          <Chip
            label={t(orderStatus[orderDetails.status]?.value || "Unknown")}
            sx={{
              backgroundColor: `${
                orderStatus[orderDetails.status]?.color || "#ccc"
              }1A`,
              color: orderStatus[orderDetails.status]?.color || "#000",
              border: `1px solid ${
                orderStatus[orderDetails.status]?.color || "#ccc"
              }`,
              padding: "0 20px",
              borderRadius: "99px",
              fontSize: "15px",
              marginTop: "5px",
            }}
          />
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
        {formattedDates.map(({ label, value }, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              gap: "0.5rem",
              alignItems: "center",
              backgroundColor: "background.gray",
              padding: "0.5rem 1rem",
              borderRadius: "8px",
            }}
          >
            <Typography variant="body2" fontWeight="bold" color="text.black">
              {t(label)}:
            </Typography>
            <Typography variant="body2" color="text.black">
              {value}
            </Typography>
          </Box>
        ))}
        {orderDetails?.status === 3 && (
          <Box
            sx={{
              marginInlineStart: "auto",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleDispatchClick}
              startIcon={<GroupRemoveIcon />}
            >
              {t("dispatchOrder")}
            </Button>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default OrderDetailsBanner;
